import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
    TabHandleProps,
    SlideOutWrapperProps,
    OverlayProps,
} from './SlideOutTab.types';

// Import Oswald font
export const globalStyles = css`
    @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&display=swap');
`;

export const SlideOutContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    z-index: 1000;
    pointer-events: none; /* Allows clicking through the container when closed */
`;

export const SlideOutWrapper = styled.div<
    SlideOutWrapperProps & { tabBackgroundColor?: string }
>`
    position: absolute;
    ${({ openPanel = 'left', isOpen }) => {
        if (openPanel === 'left') {
            return `
                left: ${isOpen ? '0' : '-100%'};
                right: auto;
            `;
        } else {
            return `
                right: ${isOpen ? '0' : '-100%'};
                left: auto;
            `;
        }
    }}
    top: 0;
    height: 100%;
    width: 90%;
    transition: ${({ openPanel = 'left' }) =>
        openPanel === 'left'
            ? 'left 0.3s ease-in-out'
            : 'right 0.3s ease-in-out'};
    display: flex;
    box-shadow: ${({ isOpen, openPanel = 'left' }) =>
        isOpen
            ? openPanel === 'left'
                ? '3px 0 8px rgba(0, 0, 0, 0.25)'
                : '-3px 0 8px rgba(0, 0, 0, 0.25)'
            : 'none'};
    ${({ isOpen, tabBackgroundColor, openPanel = 'left' }) => {
        const borderColor = tabBackgroundColor || '#E47500';
        if (isOpen) {
            if (openPanel === 'left') {
                return `border-right: 10px solid ${borderColor};`;
            } else {
                return `border-left: 10px solid ${borderColor};`;
            }
        }
        return '';
    }}
    pointer-events: auto; /* Re-enable pointer events for the panel */

    @media (max-width: 600px) {
        max-width: 100%;
    }
`;

// Base tab style for both PanelTab and StaticTab
export const BaseTab = styled.div<
    TabHandleProps & { tabBackgroundColor?: string }
>`
    background: ${({ tabBackgroundColor }) => tabBackgroundColor || '#e47500'};
    border-radius: ${({ openPanel = 'left' }) =>
        openPanel === 'left' ? '0 8px 8px 0' : '8px 0 0 8px'};
    width: 40px;
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
`;

// This tab is attached to the panel and shows when the panel is open
export const PanelTab = styled(BaseTab)`
    position: absolute;
    ${({ tabLocation = 'middle' }) => {
        if (tabLocation === 'top') {
            return 'top: 120px; transform: none;';
        } else if (tabLocation === 'bottom') {
            return 'bottom: 120px; top: auto; transform: none;';
        } else {
            return 'top: 50%; transform: translateY(-50%);';
        }
    }}
    ${
        ({ openPanel = 'left' }) =>
            openPanel === 'left'
                ? 'right: -40px; left: auto;' /* Position the tab outside the panel on the right */
                : 'left: -40px; right: auto;' /* Position the tab outside the panel on the left */
    }
`;

// This tab is fixed and shows when the panel is closed
export const StaticTab = styled(BaseTab)`
    position: fixed;
    ${({ tabLocation = 'middle' }) => {
        if (tabLocation === 'top') {
            return 'top: 120px; transform: none;';
        } else if (tabLocation === 'bottom') {
            return 'bottom: 120px; top: auto; transform: none;';
        } else {
            return 'top: 50%; transform: translateY(-50%);';
        }
    }}
    ${({ openPanel = 'left' }) =>
        openPanel === 'left'
            ? 'left: 0; right: auto;'
            : 'right: 0; left: auto;'}
    z-index: 999; /* Below the panel but above other content */
    opacity: ${(props) => (props.isOpen ? 0 : 1)};

    &:hover {
        filter: brightness(0.85);
    }
`;

export const Label = styled.span<{
    openPanel?: 'left' | 'right';
    tabTextColor?: string;
    font?: 'inherit' | 'Arial' | 'Helvetica' | 'Times New Roman';
}>`
    transform: ${({ openPanel = 'left' }) =>
        openPanel === 'right' ? 'rotate(-90deg)' : 'rotate(90deg)'};
    transform-origin: center;
    color: ${({ tabTextColor }) => tabTextColor || '#fff'};
    font-family: ${({ font }) => {
        switch (font) {
            case 'Arial':
                return 'Arial, sans-serif';
            case 'Helvetica':
                return 'Helvetica, sans-serif';
            case 'Times New Roman':
                return '"Times New Roman", serif';
            case 'inherit':
                return 'inherit';
            default:
                return 'Oswald, sans-serif';
        }
    }};
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
`;

export const ContentContainer = styled.div`
    flex: 1;
    background-color: white;
    overflow: hidden;
    height: 100%;

    /* Force the widget content to be visible within the slide-out panel */
    & > div {
        position: relative !important;
        height: 100% !important;
    }
`;

export const Overlay = styled.div<OverlayProps>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`;
